import { useState, useEffect } from "react";
import { pointsParams } from "../../../types/markup";

export const dragBoundFunc = (
  pos: any,
  frameHeight: number,
  frameWidth: number
) => {
  let newY = pos.y;
  let newX = pos.x;

  if (pos.y > frameHeight) {
    newY = frameHeight;
  }

  if (pos.y < 0) {
    newY = 0;
  }

  if (pos.x > frameWidth) {
    newX = frameWidth;
  }

  if (pos.x < 0) {
    newX = 0;
  }

  return {
    x: Math.round(newX),
    y: Math.round(newY),
  };
};

export function calculateDenominator(points: pointsParams[]): number {
  return Math.sqrt(
    Math.pow(points[0].x - points[1].x, 2) +
      Math.pow(points[0].y - points[1].y, 2)
  );
}

export function getCenterPoint(points: pointsParams[]): number[] {
  return [(points[0].x + points[1].x) / 2, (points[0].y + points[1].y) / 2];
}

export function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
        result[3],
        16
      )}`
    : "0, 0, 0";
}

type coordinates = { x: number; y: number };

export const lineLength = (a: coordinates, b: coordinates) =>
  Math.sqrt((b.y - a.y) ** 2 + (b.x - a.x) ** 2);

export const squarePolygon = (points: pointsParams[]) => {
  let result = 0;

  for (let i = 0; i < points.length - 1; i++) {
    result += points[i].x * points[i + 1].y - points[i + 1].x * points[i].y;
  }
  result +=
    points[points.length - 1].x * points[0].y -
    points[0].x * points[points.length - 1].y;

  return Math.abs(result) / 2;
};

export function useImage(
  url: string,
  crossOrigin: string,
  config = { ext: "png" }
): [image: HTMLImageElement | undefined, status: string] {
  const [image, setImage] = useState<HTMLImageElement>();
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!url) return;

    // let imgSrc = url;
    // // if it's base64, make sure it starts with the prefix
    // if (!url.startsWith("http") && !url.startsWith("data:")) {
    //   imgSrc = `data:image/${config.ext};base64,${url}`;
    // }

    const imageLoader = new Image();

    fetch(url).then(({ blob }) => {
      blob().then((result) => {
        const urlCreator = window.URL || window.webkitURL;
        const imageUrl = urlCreator.createObjectURL(result);

        imageLoader.addEventListener("load", onload);
        imageLoader.addEventListener("error", onerror);
        // crossOrigin && imageLoader.setAttribute("crossOrigin", crossOrigin);
        imageLoader.src = imageUrl;
      });
    });

    const onload = () => {
      setImage(imageLoader);
      setStatus("loaded");
    };

    const onerror = (e: any) => {
      console.error(e);
      setImage(undefined);
      setStatus("failed");
    };

    return function cleanup() {
      imageLoader.removeEventListener("load", onload);
      imageLoader.removeEventListener("error", onerror);
      setImage(undefined);
      setStatus("loading");
    };
  }, [url, crossOrigin, setImage, setStatus, config.ext]);

  return [image, status];
}

export function isPointInsidePolygon(
  point: pointsParams,
  polygon: pointsParams[]
) {
  const x = point.x,
    y = point.y;

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;

    const intersect =
      yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
}
